import { useState } from "react";
import { copyToClipboard, formatMoney } from "./payoutsFormat.js";
import {
  FilterChip,
  Row,
  RowIcon,
  RowBody,
  RowTitle,
  RowSubtitle,
  RowRight,
  Amount,
  StatusPill,
  CopyButton,
  DateChipWrapper,
  SheetOverlay,
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetCloseButton,
  OptionsCard,
  OptionRow,
  OptionHeader,
  OptionLabel,
  RadioOuter,
  RadioInner,
  CustomDateRow,
  DateField,
  DateFieldTitle,
  DateFieldInput,
  ApplyButton,
} from "./styles.payouts.js";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const toYyyyMmDd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatChipDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return "";
  const [y, m, d] = yyyyMmDd.split("-");
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
};

const DURATION_PRESETS = [
  { key: "last_7", label: "Last 7 days", days: 7 },
  { key: "last_30", label: "Last 30 days", days: 30 },
  { key: "last_6m", label: "Last 6 months", months: 6 },
  { key: "custom", label: "Chose date" },
];

const computePresetRange = (preset) => {
  const to = new Date();
  const from = new Date();
  if (preset.days) {
    from.setDate(to.getDate() - preset.days);
  } else if (preset.months) {
    from.setMonth(to.getMonth() - preset.months);
  }
  return { from: toYyyyMmDd(from), to: toYyyyMmDd(to) };
};

export const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="24 46 20 20" fill="none" aria-hidden="true">
    <path
      d="M37.0399 50.8799C37.3199 51.1799 37.3199 51.6599 37.0399 51.9399L32.5599 56.4199L37.0399 60.8799C37.3199 61.1799 37.3199 61.6599 37.0399 61.9399C36.7599 62.2199 36.2599 62.2399 35.9799 61.9399L30.9799 56.9399C30.6799 56.6599 30.6799 56.1799 30.9799 55.8799L35.9799 50.8799C36.2599 50.5999 36.7399 50.5999 37.0399 50.8799Z"
      fill="#293142"
    />
  </svg>
);

export const SearchIcon = () => (
  <svg width="16" height="16" viewBox="318 48 16 16" fill="none" aria-hidden="true">
    <path
      d="M325.016 49.5738C322.011 49.5738 319.574 52.0105 319.574 55.0164C319.574 58.0223 322.011 60.459 325.016 60.459C328.022 60.459 330.459 58.0223 330.459 55.0164C330.459 52.0105 328.022 49.5738 325.016 49.5738ZM318 55.0164C318 51.1413 321.141 48 325.016 48C328.891 48 332.033 51.1413 332.033 55.0164C332.033 56.6704 331.46 58.1908 330.503 59.3902L333.77 62.6567C334.077 62.964 334.077 63.4622 333.77 63.7695C333.462 64.0768 332.964 64.0768 332.657 63.7695L329.39 60.503C328.191 61.4605 326.67 62.0328 325.016 62.0328C321.141 62.0328 318 58.8914 318 55.0164Z"
      fillRule="evenodd"
      clipRule="evenodd"
      fill="#293142"
    />
  </svg>
);

export const DownloadIcon = () => (
  <svg width="22" height="22" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M8.748 12.996C8.658 12.96 8.586 12.906 8.532 12.852L4.032 8.352C3.762 8.082 3.762 7.65 4.032 7.398C4.302 7.146 4.716 7.128 4.986 7.398L8.334 10.746V0.666C8.334 0.306 8.622 0 9 0C9.378 0 9.684 0.306 9.684 0.666V10.746L13.032 7.398C13.284 7.128 13.716 7.128 13.986 7.398C14.256 7.668 14.238 8.082 13.986 8.352L9.486 12.852C9.414 12.906 9.342 12.96 9.252 12.996C9.18 13.032 9.09 13.05 9 13.05C8.91 13.05 8.82 13.032 8.748 12.996ZM16.65 11.466C16.65 11.106 16.956 10.8 17.334 10.8C17.712 10.8 18 11.106 18 11.466V15.516C18 16.884 16.866 18 15.48 18H2.52C1.134 18 0 16.884 0 15.516V11.466C0 11.106 0.306 10.8 0.684 10.8C1.062 10.8 1.35 11.106 1.35 11.466V15.516C1.35 16.146 1.872 16.65 2.52 16.65H15.48C16.128 16.65 16.65 16.146 16.65 15.516V11.466Z"
      fill="currentColor"
    />
  </svg>
);

export const DateRangeChip = ({ from, to, onApply }) => {
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("custom");
  const [draftFrom, setDraftFrom] = useState(from || "");
  const [draftTo, setDraftTo] = useState(to || "");

  const label =
    from && to
      ? `Due: ${formatChipDate(from)} to ${formatChipDate(to)}`
      : "Due date";

  const openSheet = () => {
    setDraftFrom(from || "");
    setDraftTo(to || "");
    setSelectedPreset("custom");
    setOpen(true);
  };

  const closeSheet = () => setOpen(false);

  const handleSelectPreset = (preset) => {
    setSelectedPreset(preset.key);
    if (preset.key !== "custom") {
      const range = computePresetRange(preset);
      setDraftFrom(range.from);
      setDraftTo(range.to);
    } else {
      setDraftFrom((prev) => prev || from || "");
      setDraftTo((prev) => prev || to || "");
    }
  };

  const canApply = draftFrom && draftTo && draftFrom <= draftTo;

  const handleApply = () => {
    if (!canApply) return;
    onApply({ from: draftFrom, to: draftTo });
    setOpen(false);
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <DateChipWrapper>
      <FilterChip active={Boolean(from && to)} onClick={openSheet}>
        {label} ▾
      </FilterChip>
      {open ? (
        <SheetOverlay onClick={closeSheet}>
          <Sheet onClick={stopPropagation}>
            <SheetHeader>
              <SheetTitle>Select duration</SheetTitle>
              <SheetCloseButton onClick={closeSheet} aria-label="Close">
                ✕
              </SheetCloseButton>
            </SheetHeader>
            <OptionsCard>
              {DURATION_PRESETS.map((preset) => {
                const active = selectedPreset === preset.key;
                return (
                  <OptionRow
                    key={preset.key}
                    onClick={() => handleSelectPreset(preset)}
                  >
                    <OptionHeader>
                      <OptionLabel>{preset.label}</OptionLabel>
                      <RadioOuter active={active}>
                        <RadioInner active={active} />
                      </RadioOuter>
                    </OptionHeader>
                    {preset.key === "custom" && active ? (
                      <CustomDateRow onClick={stopPropagation}>
                        <DateField>
                          <DateFieldTitle>From</DateFieldTitle>
                          <DateFieldInput
                            type="date"
                            value={draftFrom}
                            onChange={(e) => setDraftFrom(e.target.value)}
                          />
                        </DateField>
                        <DateField>
                          <DateFieldTitle>To</DateFieldTitle>
                          <DateFieldInput
                            type="date"
                            value={draftTo}
                            onChange={(e) => setDraftTo(e.target.value)}
                          />
                        </DateField>
                      </CustomDateRow>
                    ) : null}
                  </OptionRow>
                );
              })}
            </OptionsCard>
            <ApplyButton disabled={!canApply} onClick={handleApply}>
              Apply
            </ApplyButton>
          </Sheet>
        </SheetOverlay>
      ) : null}
    </DateChipWrapper>
  );
};

// Tag + subtitle rules for a payment row:
//   tag comes from the backend (Paid, Adjusted, Upcoming, Carry forward)
//   payout_utr_number present -> subtitle: "UTR: <utr>" (copyable)
//   tag = "Carry forward"     -> subtitle: "Will be adjusted in next payout"
//   otherwise                 -> no subtitle (date centers vertically)
const getPaymentTagAndSubtitle = (item) => {
  const tag = item.tag;
  const utr = item.payout_utr_number;

  if (utr) {
    const displayUtr = utr.length > 20 ? `${utr.slice(0, 18)}..` : utr;
    return { tag, subtitle: `UTR: ${displayUtr}`, showCopy: true };
  }
  return {
    tag,
    subtitle:
      (tag || "").toLowerCase() === "carry forward"
        ? "Will be adjusted in next payout"
        : "",
    showCopy: false,
  };
};

export const PaymentRow = ({ item, onClick }) => {
  const isDebit = Number(item.payout_amount) < 0;
  const { tag, subtitle, showCopy } = getPaymentTagAndSubtitle(item);
  return (
    <Row onClick={onClick} clickable={Boolean(onClick)}>
      <RowIcon>
        <img src="/logos/rupee_icon.png" alt="" />
      </RowIcon>
      <RowBody>
        <RowTitle>{formatDate(item.accrual_date)}</RowTitle>
        {subtitle ? (
          <RowSubtitle>
            {subtitle}
            {showCopy ? (
              <CopyButton
                type="button"
                onClick={() => copyToClipboard(item.payout_utr_number)}
                aria-label="Copy UTR"
              >
                ⎘
              </CopyButton>
            ) : null}
          </RowSubtitle>
        ) : null}
      </RowBody>
      <RowRight>
        <Amount negative={isDebit}>{formatMoney(item.payout_amount, { spaced: true })}</Amount>
        {tag ? <StatusPill status={tag}>{tag}</StatusPill> : null}
      </RowRight>
    </Row>
  );
};

// Tag + amount rules for a transaction row:
//   tag comes from the backend (Paid, Adjusted, Upcoming, Carry forward, Hold)
//
//   Amount: transaction_amount exactly as the backend sends it
//
//   Amount suffix:
//     transaction_type = "GRN_COMPLETE" AND transaction_status != "SUCCESS"
//       -> append "(estd.)" in gray (estimated GRN amount, not yet finalized)
export const TransactionRow = ({ item, onClick }) => {
  const transactionStatus = (item.transaction_status || "").toUpperCase();
  const transactionType = (item.transaction_type || "").toUpperCase();
  const tag = item.tag;
  const showEstimated =
    transactionType === "GRN_COMPLETE" && transactionStatus !== "SUCCESS";
  const dueLabel = item.consideration_date
    ? `Due ${formatDate(item.consideration_date)}`
    : item.actual_payment_date
    ? `Paid on ${formatDate(item.actual_payment_date)}`
    : "";
  const label =
    item.display_transaction_type ||
    item.parent_transaction_type ||
    item.transaction_type ||
    "Invoice";
  return (
    <Row onClick={onClick} clickable={Boolean(onClick)}>
      <RowIcon>
        <img src="/logos/transaction_document.png" alt="" />
      </RowIcon>
      <RowBody>
        <RowTitle>{label}</RowTitle>
        <RowSubtitle>{item.transaction_number}</RowSubtitle>
        {dueLabel ? <RowSubtitle>{dueLabel}</RowSubtitle> : null}
      </RowBody>
      <RowRight>
        <Amount>
          {formatMoney(item.transaction_amount, { spaced: true })}
          {showEstimated ? (
            <span style={{ color: "#9197a6", marginLeft: 4, fontWeight: 400 }}>
              (est.)
            </span>
          ) : null}
        </Amount>
        {tag ? <StatusPill status={tag}>{tag}</StatusPill> : null}
      </RowRight>
    </Row>
  );
};
